import React, { useState, useRef } from "react";
import { ChevronDown, Check, X } from "lucide-react";

// Asignación rápida de responsable(s) desde el listado o la agenda.
// `value` es un string separado por comas. Seleccionar un nombre lo ALTERNA
// (añade/quita) en la lista, preservando co-responsables. El popover se
// posiciona con position:fixed para no quedar recortado por contenedores con
// overflow (tabla con scroll). `variant`: "cell" (listado) | "chip" (agenda).
export default function ResponsablePicker({
  value = "", equipo = [], onChange,
  variant = "cell", placeholder = "Asignar…", testid,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  const names = String(value || "").split(",").map(s => s.trim()).filter(Boolean);
  const nameSet = new Set(names.map(n => n.toLowerCase()));
  const display = names.join(", ");

  const toggle = (n) => {
    const exists = nameSet.has(n.toLowerCase());
    const next = exists ? names.filter(x => x.toLowerCase() !== n.toLowerCase()) : [...names, n];
    onChange(next.join(", "));
  };
  const clearAll = () => { onChange(""); setOpen(false); };

  const openPopover = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const width = 224;
      const belowSpace = window.innerHeight - r.bottom;
      const openUp = belowSpace < 260;
      setPos({
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
        top: openUp ? undefined : r.bottom + 4,
        bottom: openUp ? (window.innerHeight - r.top + 4) : undefined,
        width,
      });
    }
    setOpen(o => !o);
  };

  const list = equipo
    .filter(n => !q || n.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 8);

  const btnCls = variant === "chip"
    ? `inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded max-w-[150px] truncate ${display ? "bg-gray-100 text-gray-700" : "text-gray-400 italic border border-dashed border-gray-300"} hover:border-[#007934] hover:text-[#007934]`
    : `inline-flex items-center gap-1 text-left max-w-[170px] ${display ? "text-gray-700" : "text-gray-400 italic"} hover:text-[#007934]`;

  return (
    <>
      <button ref={btnRef} type="button" onClick={openPopover} className={btnCls}
        title={display || "Asignar responsable"} data-testid={testid}>
        <span className="truncate">{display || placeholder}</span>
        <ChevronDown size={variant === "chip" ? 10 : 11} className="shrink-0" />
      </button>
      {open && pos && (
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
          <div className="fixed z-[91] bg-white border border-gray-200 rounded-lg shadow-xl p-2"
            style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width }}
            data-testid={testid ? `${testid}-popover` : undefined}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)}
              placeholder="Buscar o escribir…"
              className="w-full border rounded px-2 py-1 text-xs mb-1"
              data-testid={testid ? `${testid}-search` : undefined}
              onKeyDown={e => { if (e.key === "Enter" && q.trim()) { toggle(q.trim()); setQ(""); } }} />
            <div className="max-h-44 overflow-y-auto">
              {list.map(n => {
                const sel = nameSet.has(n.toLowerCase());
                return (
                  <button key={n} type="button" onClick={() => toggle(n)}
                    className="flex items-center justify-between w-full text-left px-2 py-1 text-xs rounded hover:bg-emerald-50"
                    data-testid={testid ? `${testid}-opt` : undefined}>
                    <span className="truncate">{n}</span>
                    {sel && <Check size={12} className="text-emerald-600" />}
                  </button>
                );
              })}
              {q.trim() && !equipo.some(n => n.toLowerCase() === q.trim().toLowerCase()) && (
                <button type="button" onClick={() => { toggle(q.trim()); setQ(""); }}
                  className="w-full text-left px-2 py-1 text-xs rounded hover:bg-emerald-50 text-[#007934]">
                  + Usar "{q.trim()}"
                </button>
              )}
            </div>
            {display && (
              <button type="button" onClick={clearAll}
                className="flex items-center gap-1 w-full text-left px-2 py-1 mt-1 text-[11px] rounded hover:bg-rose-50 text-rose-600 border-t pt-1"
                data-testid={testid ? `${testid}-clear` : undefined}>
                <X size={11} /> Quitar responsable
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}
